import { ReactElement, useMemo } from 'react';
import clsx from 'clsx';
import { ReactComponent as KibanaIcon } from '@assets/images/rdash/kibana.svg';
import { ReactComponent as AudioTextQuestionTypeIcon } from '@assets/images/survey/audio-text-question.svg';
import { ReactComponent as VasQuestionTypeIcon } from '@assets/images/survey/vas-question.svg';

import { normalizeIcon } from '$app-web/utils/font-awesome-library';

import classes from './styles.module.scss';

// eslint-disable-next-line import/no-extraneous-dependencies
import { FontAwesomeIcon } from '$common/fontawesome-pro/react-fontawesome';

interface Props {
    icon?: string | Array<string> | { iconName: string; prefix: string };
    customIcon?: string;
    size?: 'xs' | 'sm' | 'md' | 'lg';
    color?: string;
    disabled?: boolean;
    className?: string;
    iconProps?: Record<string, unknown>;
    fixedWidth?: boolean;
}

const normalizeIconProp = (icon: Props['icon']): Props['icon'] => {
    if (Array.isArray(icon) && icon.length === 2) {
        return [icon[0], normalizeIcon(icon[1])];
    }
    if (typeof icon === 'string') {
        return normalizeIcon(icon);
    }
    if (icon && typeof icon === 'object' && 'iconName' in icon) {
        return { ...icon, iconName: normalizeIcon(icon.iconName) };
    }
    return icon;
};

const Icon = (props: Props): ReactElement => {
    const { size = 'md', color = 'gray-500', disabled = false, className, icon: rawIcon, customIcon, ...restProps } = props;
    const icon = normalizeIconProp(rawIcon);

    const CustomIconComponent = useMemo(() => {
        switch (customIcon) {
            case 'kibana':
                return KibanaIcon;
            case 'audio-text':
                return AudioTextQuestionTypeIcon;
            case 'vas':
                return VasQuestionTypeIcon;
            default:
                return null;
        }
    }, [customIcon]);

    const getCommonIconProps = () => ({
        className: clsx('color', {
            [classes['space-200']]: disabled,
            [color]: !disabled,
            // TODO: Get rid of the following line after making the color system more consistent:
            [classes[color]]: !disabled,
        }),
        ...restProps,
    });

    return (
        <div className={clsx(classes.root, classes[size], className)}>
            {customIcon && CustomIconComponent !== null ? (
                <CustomIconComponent {...getCommonIconProps()} />
            ) : (
                <FontAwesomeIcon fixedWidth icon={icon} {...getCommonIconProps()} />
            )}
        </div>
    );
};

export default Icon;
